import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ChevronRight,
  Clock,
  Plus,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import { Modal } from "../components/ui/Modal";
import {
  schedulesApi,
  type ScheduleKind,
  type ScheduleListItem,
} from "../api/schedulesApi";
import { HttpError } from "../api/http";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: ScheduleListItem[] }
  | { status: "error"; message: string };

/**
 * Combined overview for weekplans + calendars. Uses the same
 * PageHeader + OverviewToolbar pattern as Applications/Huntgroups so the
 * customer sidebar feels consistent. A primary "+ New schedule" action
 * in the page header opens a modal that lets the user pick a type
 * (Weekplan / Calendar) + name and creates the record before jumping to
 * its detail page.
 */
export function SchedulesPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const reload = async () => {
    setState({ status: "loading" });
    try {
      const response = await schedulesApi.list();
      setState({ status: "ready", items: response.items ?? [] });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load schedules";
      setState({ status: "error", message });
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as ScheduleListItem[];
    const q = search.trim().toLowerCase();
    const sorted = state.items.slice().sort((a, b) => {
      // Case-insensitive alphabetical primary sort, with type as a
      // stable tiebreaker so identical names fall into a predictable
      // Weekplan-before-Calendar order instead of shuffling on reload.
      const byName = a.name.localeCompare(b.name, "en", {
        sensitivity: "base",
      });
      if (byName !== 0) return byName;
      return a.kind.localeCompare(b.kind);
    });
    if (!q) return sorted;
    return sorted.filter((x) => {
      const hay = [x.name, x.description ?? "", x.kind]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [state, search]);

  const total = state.status === "ready" ? state.items.length : 0;

  const existingByKind = useMemo(() => {
    // Duplicate-name guard keyed by kind, since the DB treats weekplans
    // and calendars as separate namespaces.
    const map = new Map<ScheduleKind, Set<string>>([
      ["Weekplan", new Set<string>()],
      ["Calendar", new Set<string>()],
    ]);
    if (state.status !== "ready") return map;
    for (const x of state.items) {
      map.get(x.kind)?.add(x.name.trim().toLowerCase());
    }
    return map;
  }, [state]);

  const actions = (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => setModalOpen(true)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <Plus size={16} />
      New schedule
    </button>
  );

  return (
    <>
      <PageHeader title="Schedules" actions={actions} />
      <div className="dashboard">
        {state.status === "loading" && <LoadingState />}
        {state.status === "error" && <ErrorState message={state.message} />}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState onCreate={() => setModalOpen(true)} />
        )}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by name"
              count={{ matched: filtered.length, total }}
            />
            {filtered.length === 0 ? (
              <div className="tile" style={{ color: "var(--portal-muted)" }}>
                No schedules match this search.
              </div>
            ) : (
              <SchedulesRowList
                items={filtered}
                onOpen={(item) => {
                  if (item.kind === "Weekplan") {
                    navigate(`/schedules/weekplans/${item.id}`);
                  } else {
                    navigate(`/schedules/calendars/${item.id}`);
                  }
                }}
              />
            )}
          </>
        )}
      </div>

      <CreateScheduleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingByKind={existingByKind}
        onCreated={(kind, id) => {
          setModalOpen(false);
          if (kind === "Weekplan") {
            navigate(`/schedules/weekplans/${id}`);
          } else {
            navigate(`/schedules/calendars/${id}`);
          }
        }}
      />
    </>
  );
}

function CreateScheduleModal({
  open,
  onClose,
  existingByKind,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  existingByKind: Map<ScheduleKind, Set<string>>;
  onCreated: (kind: ScheduleKind, id: number) => void;
}) {
  const [kind, setKind] = useState<ScheduleKind>("Weekplan");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset to defaults whenever the modal is (re-)opened so previous
  // input isn't silently re-applied; mirrors the Data Sheets modal.
  useEffect(() => {
    if (!open) {
      setKind("Weekplan");
      setName("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const trimmed = name.trim();
  const existingForKind = existingByKind.get(kind) ?? new Set<string>();
  const duplicate =
    trimmed.length > 0 && existingForKind.has(trimmed.toLowerCase());
  const canSubmit = !busy && trimmed.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const created = await schedulesApi.create({ kind, name: trimmed });
      onCreated(created.kind, created.id);
    } catch (err) {
      const fallback =
        err instanceof Error
          ? err.message
          : `Failed to create ${kind.toLowerCase()}`;
      const apiDetail =
        err instanceof HttpError && typeof err.data === "object" && err.data
          ? (err.data as { error?: string; detail?: string }).detail ?? null
          : null;
      setError(apiDetail ?? fallback);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New schedule"
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} />
            {busy ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: "grid", gap: 12 }}
      >
        <label style={{ display: "grid", gap: 4 }}>
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--portal-muted)",
              fontWeight: 600,
            }}
          >
            Type
          </span>
          <div style={{ position: "relative" }}>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ScheduleKind)}
              disabled={busy}
              style={{
                appearance: "none",
                width: "100%",
                padding: "8px 32px 8px 12px",
                border: "1px solid var(--portal-border)",
                borderRadius: 6,
                background: "#ffffff",
                fontFamily: "inherit",
                fontSize: "0.95rem",
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              <option value="Weekplan">Weekplan</option>
              <option value="Calendar">Calendar</option>
            </select>
            <ChevronDown
              size={14}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "var(--portal-muted)",
              }}
            />
          </div>
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--portal-muted)",
              fontWeight: 600,
            }}
          >
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            placeholder={
              kind === "Weekplan"
                ? "e.g. Office hours"
                : "e.g. Public holidays 2026"
            }
            maxLength={50}
            autoFocus
            style={{
              padding: "8px 12px",
              border: "1px solid var(--portal-border)",
              borderRadius: 6,
              fontSize: "0.95rem",
              fontFamily: "inherit",
              background: "#ffffff",
            }}
          />
        </label>
        {duplicate && (
          <div style={{ fontSize: "0.85rem", color: "#8a6d00" }}>
            A {kind.toLowerCase()} named "{trimmed}" already exists. It will
            still be created as a new entry.
          </div>
        )}
        {error && (
          <div style={{ fontSize: "0.85rem", color: "#c0392b" }}>{error}</div>
        )}
        <button type="submit" style={{ display: "none" }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

function SchedulesRowList({
  items,
  onOpen,
}: {
  items: ScheduleListItem[];
  onOpen: (item: ScheduleListItem) => void;
}) {
  return (
    <div className="tile-rows">
      {items.map((item) => (
        <button
          key={`${item.kind}-${item.id}`}
          type="button"
          className="tile tile-link"
          onClick={() => onOpen(item)}
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            padding: "12px 18px",
            textAlign: "left",
          }}
          aria-label={`Open ${item.kind.toLowerCase()} ${item.name}`}
        >
          <div
            className="tile-row-icon"
            style={{ flexShrink: 0 }}
            aria-hidden="true"
          >
            {item.kind === "Weekplan" ? (
              <Clock size={18} />
            ) : (
              <CalendarDays size={18} />
            )}
          </div>
          <div style={{ flexShrink: 0 }}>
            <TypeBadge kind={item.kind} />
          </div>
          <div
            style={{
              flex: "2 1 0",
              minWidth: 0,
              fontWeight: 600,
              fontSize: "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.name}
          >
            {item.name || "—"}
          </div>
          <div
            className="tile-row-detail"
            style={{
              flex: "2 1 0",
              minWidth: 0,
              color: "var(--portal-muted)",
              fontSize: "0.9rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.description ?? undefined}
          >
            {item.description || ""}
          </div>
          <div style={{ flexShrink: 0 }}>
            <LiveBadge kind={item.kind} isLive={item.isLive} />
          </div>
          <ChevronRight
            size={16}
            style={{ color: "var(--portal-primary)", flexShrink: 0 }}
          />
        </button>
      ))}
    </div>
  );
}

function TypeBadge({ kind }: { kind: ScheduleKind }) {
  const isWeekplan = kind === "Weekplan";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid var(--portal-border)",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: "var(--portal-surface-alt)",
        color: "var(--portal-text-soft)",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {isWeekplan ? <Clock size={12} /> : <CalendarRange size={12} />}
      {isWeekplan ? "Weekplan" : "Calendar"}
    </span>
  );
}

function LiveBadge({ kind, isLive }: { kind: ScheduleKind; isLive: boolean }) {
  // Use the same green-success palette as the active-app chip on the
  // Applications list; muted grey when not live so the eye lands on the
  // active rows first.
  const styles: React.CSSProperties = isLive
    ? {
        background: "var(--portal-status-success-soft)",
        color: "var(--portal-status-success-darker)",
        borderColor: "var(--portal-status-success)",
      }
    : {
        background: "var(--portal-surface-alt)",
        color: "var(--portal-muted)",
        borderColor: "var(--portal-border)",
      };
  const label = isLive
    ? kind === "Weekplan"
      ? "Open now"
      : "Today"
    : kind === "Weekplan"
    ? "Closed"
    : "No date today";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid",
        fontSize: "0.78rem",
        fontWeight: 600,
        ...styles,
      }}
    >
      {label}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="tile" style={{ color: "var(--portal-muted)" }}>
      Loading schedules…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="tile"
      style={{
        borderColor: "#e74c3c",
        color: "#c0392b",
        background: "#fdecea",
      }}
    >
      <strong>Something went wrong.</strong>
      <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{message}</div>
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <CalendarRange size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>No schedules yet</h3>
        <p style={{ marginTop: 0 }}>
          Schedules cover weekly open/closed hours and dated exceptions
          (holidays, special days). Click <strong>+ New schedule</strong> to
          create your first weekplan or calendar.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onCreate}
          style={{
            marginTop: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Plus size={16} />
          New schedule
        </button>
      </div>
    </section>
  );
}
